import { faFiles } from '@fortawesome/pro-solid-svg-icons/faFiles';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons/faCircleNotch';
import { faFile } from '@fortawesome/pro-solid-svg-icons/faFile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GetReadableFileSize } from '../../../Constants/Helpers';
import { ColourMod, FlexGap, FlexJustify } from '../../../Data/Enums/LayoutEnums';
import { PrepareFiles } from './UploadFileHelpers';
import { UploadBlockType } from './UploadFileTypes';
import LaptopImage from '../../../Images/LaptopImage';
import EnvelopeImage from '../../../Images/EnvelopeImage';
import StackHorizontal from '../../Wrappers/StackHorizontal';
import AlertBanner from '../../AlertBanner/AlertBanner';
import { UploadFileEntriesDragDrop } from './UploadFileEntriesDragDrop';
import { UploadFileEntries } from './UploadFileEntries';
import { TabNavigation } from '../../Tabs/Tabs';
import { maxCloudflareFileSizeInBytes } from '../../../../Api/SendMailEndpoints';

function UploadFile(props: UploadBlockType) {
	const [fileErrors, setFileErrors] = useState<any>({
		fileNames: [],
		state: false,
		colour: ColourMod.Alert,
		title: '',
		message: '',
	});

	const [useMergeMode, setUseMergeMode] = useState<boolean>(props.useBulkMode !== true);

	const [files, setFiles] = useState<Array<File>>([]);
	const [maxFiles, setMaxFiles] = useState<number>(props.useBulkMode !== true ? props.maxFiles : 1000);
	const [maxSize, setMaxSize] = useState<number>(props.maxSizeInBytes);

	const acceptsFileExtension = props.acceptsFileExtension;
	const acceptsMimeType = props.acceptsMimeType;

	const inputRef = useRef<HTMLInputElement>(null);
	const dropZoneRef = useRef<HTMLDivElement>(null);

	const ResetFiles = () => {
		setFileErrors({
			state: false,
			colour: ColourMod.Alert,
			title: '',
			message: '',
		});

		props.onError(false);

		setFiles([]);
		setUseMergeMode(props.useBulkMode !== true);
		setMaxFiles(props.useBulkMode !== true ? props.maxFiles : 1000);
		setMaxSize(props.maxSizeInBytes);

		if (props.onModeChange) {
			props.onModeChange(props.useBulkMode !== true);
		}

		if (inputRef.current) {
			inputRef.current.value = '';
		}
	};

	const HandleFileChange = useCallback(
		(filesChanged: FileList | Array<File> | null, removedFile = false) => {
			const preparedFiles = PrepareFiles(
				{
					existingFiles: removedFile ? [] : files,
					selectedFiles: filesChanged || [],
					singleFile: false,
					mergeFiles: useMergeMode,
					maxFiles,
					maxSize,
					acceptsFileExtension,
					acceptsMimeType,
					onChangeCallback: (newFiles) => {
						props.onChange(newFiles);
					},
				},
				!useMergeMode
			);

			props.onError(preparedFiles.errors.state);
			setFileErrors(preparedFiles.errors);
			setFiles(preparedFiles.files);
			props.onChange(preparedFiles.files);
		},
		[files, maxFiles, maxSize, useMergeMode]
	);

	const DragEnter = (e: React.DragEvent) => {
		if (!dropZoneRef.current?.classList.contains('drop-active')) {
			dropZoneRef.current?.classList.add('drop-active');
		}
	};

	const DragLeave = (e: React.DragEvent) => {
		if (dropZoneRef.current?.classList.contains('drop-active')) {
			dropZoneRef.current.classList.remove('drop-active');
		}
	};

	const DragDrop = (e: React.DragEvent) => {
		e.preventDefault();
		HandleFileChange(e.dataTransfer?.files || null);

		if (dropZoneRef.current?.classList.contains('drop-active')) {
			dropZoneRef.current.classList.remove('drop-active');
		}
	};

	const HandleFileRemove = useCallback(
		(index: number) => {
			const newFiles = [...files];
			newFiles.splice(index, 1);

			HandleFileChange(newFiles, true);
		},
		[files]
	);

	const HandleInputTrigger = () => {
		if (inputRef.current) {
			inputRef.current.value = '';
			inputRef.current.click();
		}
	};

	const HandleMergeModeChange = (state: boolean) => {
		setUseMergeMode(state);
		if (props.onModeChange) {
			props.onModeChange(state);
		}

		setMaxFiles(state ? props.maxFiles : 1000);
		setMaxSize(props.maxSizeInBytes);
	};

	useEffect(() => {
		if (useMergeMode) {
			setMaxSize(files.length > 1 ? maxCloudflareFileSizeInBytes : props.maxSizeInBytes);
		} else {
			setMaxSize(props.maxSizeInBytes);
		}
	}, [files, useMergeMode, props.maxSizeInBytes]);

	useEffect(() => {
		ResetFiles();
	}, [props.reset]);

	useEffect(() => {
		HandleFileChange(null);
	}, [maxSize, maxFiles, useMergeMode]);

	return (
		<div
			className={`upload-file-wrapper ${props.useBulkMode ? 'upload-file-bulk' : ''} ${
				props.complexInteractions ? 'upload-file-complex' : ''
			}`}
		>
			{props.useBulkMode && (
				<>
					<label>Send Mode</label>
					<TabNavigation
						detailed
						data={[
							{
								title: 'Bulk Send',
								description:
									'Each file will be sent as an individual Send. This mode will not check for, or prevent, duplicate file uploads.',
								icon: <FontAwesomeIcon icon={faFiles} size={'lg'} />,
								content: '',
								disabled: false,
							},
							{
								title: 'Single Send',
								description:
									'Multiple files files will be merged into a single Send. The file order can be changed via drag and drop.',
								icon: <FontAwesomeIcon icon={faFile} size={'lg'} />,
								content: '',
								disabled: false,
							},
						]}
						activeIndex={useMergeMode ? 1 : 0}
						handleChangeCallback={(e: number) => HandleMergeModeChange(e === 1)}
					/>
				</>
			)}

			<div className={'upload-file-inner-wrapper'}>
				<div
					ref={dropZoneRef}
					onClick={() => HandleInputTrigger()}
					onKeyDown={(e) => (e.code === 'Enter' ? HandleInputTrigger() : null)}
					tabIndex={0}
					role='button'
					aria-label={`${props.name} File Upload`}
					className={`${'upload-block'} ${props.passive ? 'upload-hoverable' : ''}`}
					onDrop={(e) => DragDrop(e)}
					onDragOver={(e) => e.preventDefault()}
					onDragLeave={(e) => DragLeave(e)}
					onDragEnter={(e) => DragEnter(e)}
				>
					<div className={'hover-swapper'}>
						{props.passive && (
							<div className={'default-container'}>
								<span className={'loader-fg'}>
									<FontAwesomeIcon icon={faCircleNotch} fixedWidth spin size='2x' />
								</span>
								<span
									data-default={props.passiveLabel ? props.passiveLabel : 'Waiting'}
									data-processing={'Processing'}
									className={'upload-status'}
								/>
							</div>
						)}

						<div className={`${props.passive ? 'hovered-container' : 'default-container'}`}>
							<div className={'upload-control-icon-wrapper'}>
								<span className={'icon-bg'}>
									<LaptopImage />
								</span>
								<span className={'icon-fg'}>
									<EnvelopeImage />
								</span>
							</div>

							<span className={'upload-block-label'}>
								<strong>
									{useMergeMode
										? `Drop or choose up to ${maxFiles} PDFs to merge into a single Send`
										: `Drop or choose up to ${maxFiles} PDFs to send`}
								</strong>
							</span>
						</div>
					</div>

					<StackHorizontal
						gap={FlexGap.Medium}
						justify={FlexJustify.Between}
						extraClass={`fade ${'upload-meta'}`}
					>
						{!props.hideFileExtension && props.acceptsFileExtension && (
							<div>
								<small>
									<strong>File Extensions:</strong>
								</small>
								<br />
								{`${props.acceptsFileExtension?.toString().replace(/,/g, ' ')}`}
							</div>
						)}

						{props.acceptsMimeType && (
							<div>
								<small>
									<strong>File Types:</strong>
								</small>
								<br />
								{`${props.acceptsMimeType?.toString().replace(/,/g, ' ')}`}
							</div>
						)}

						{useMergeMode && maxFiles > 1 ? (
							<div className={'upload-file-size-scope'}>
								<div
									className={`upload-file-size-entry ${
										files.length <= 1 ? 'upload-file-size-entry-active' : ''
									}`}
								>
									<small>
										<strong>Max Single File Size:</strong>
									</small>
									<div>{GetReadableFileSize(props.maxSizeInBytes)}</div>
								</div>
								<div
									className={`upload-file-size-entry ${
										files.length > 1 ? 'upload-file-size-entry-active' : ''
									}`}
								>
									<small>
										<strong>Max Size Per File:</strong>
									</small>
									<div>{GetReadableFileSize(maxCloudflareFileSizeInBytes)}</div>
								</div>
							</div>
						) : (
							<div>
								<small>
									<strong>Max Size Per File:</strong>
								</small>
								<br />
								{`${GetReadableFileSize(maxSize)}`}
							</div>
						)}
					</StackHorizontal>

					<input
						ref={inputRef}
						onChange={(e) => (e.target.files ? HandleFileChange(e.target.files) : undefined)}
						type='file'
						className={'custom-file-input'}
						name={props.name}
						multiple={!props.singleFileOnly}
						accept={props.acceptsFileExtension?.toString() || props.acceptsMimeType?.toString()}
						tabIndex={-1}
						aria-label={`${props.name} File Upload Input`}
					/>
				</div>

				{props.componentError && props.componentError.state && (
					<AlertBanner
						title={props.componentError.title}
						content={props.componentError.message}
						colourMod={ColourMod.Alert}
					/>
				)}

				{fileErrors.state && (
					<AlertBanner
						title={
							useMergeMode
								? fileErrors.title
								: `Selected files that are over ${GetReadableFileSize(maxSize)}`
						}
						content={useMergeMode ? fileErrors.message : 'Please remove them to continue'}
						colourMod={fileErrors.colour}
					/>
				)}

				{files?.length > 0 && !props.componentError?.state && !fileErrors.state && (
					<div className={'upload-file-selected-header'}>
						{props.selectedFilesHeader
							? props.selectedFilesHeader
							: `Selected Files: ${maxFiles ? `${files?.length}/${maxFiles}` : files?.length}`}
					</div>
				)}

				<div className={`upload-selected-files-wrapper`}>
					{files?.length > 0 && useMergeMode && (
						<UploadFileEntriesDragDrop
							files={files}
							fileErrors={fileErrors}
							extensionDisplayValue={props.extensionDisplayValue}
							updateFilesCallback={(newFiles) => {
								setFiles(newFiles);
								props.onChange(newFiles);
							}}
							removeFileCallback={(index) => HandleFileRemove(index)}
							maxFiles={maxFiles}
							maxSize={maxSize}
							selectedFilesHeader={props.selectedFilesHeader}
						/>
					)}

					{files?.length > 0 && !useMergeMode && (
						<UploadFileEntries
							files={files}
							fileErrors={fileErrors}
							extensionDisplayValue={props.extensionDisplayValue}
							removeFileCallback={(index) => HandleFileRemove(index)}
							maxFiles={maxFiles}
							maxSize={maxSize}
							selectedFilesHeader={props.selectedFilesHeader}
						/>
					)}
				</div>
			</div>
		</div>
	);
}

export { UploadFile };
